import { useMemo, type MouseEvent } from 'react'
import type { CalendarEvent, GoogleMeetMeeting, RecallDetectedMeeting } from '@highlight/common'
import { useLinkedAudioNotes } from '@renderer/hooks/audio-notes'
import { useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import {
  useDetectedMeetingForEvent,
  useIsRecordingCalendarEvent,
} from '@renderer/hooks/audio/use-recording-calendar-event'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { isWithinNextMinutes } from '@shared/utils/DateUtils'
import { calendarEventToScheduledMeeting } from '@shared/utils/connections/calendars'
import { useViewActions } from '../../view-atoms'
import { findSummaryReadyNote, isJoinWindowOpen, useOpenMeetingSummary } from './meeting-quick-actions'
import { MEETING_VIEW_ID } from './meeting-view-id'
import { useJoinMeeting } from './use-join-meeting'
import { useLiveMeetingIdentity } from './use-live-meeting-identity'

export interface MeetingCallToAction {
  label: string
  /** Takes the inverse fill, which marks the meeting to be in right now. */
  isProminent: boolean
  /** Join before its window opens, which renders grey. */
  isEarly?: boolean
  isPending?: boolean
  onSelect: (event: MouseEvent) => void
}

export interface MeetingCallsToAction {
  /** The one move the meeting asks for right now, if any. */
  primary: MeetingCallToAction | undefined
  /** Opens the summary of a note saved earlier today. */
  viewSummary: ((event: MouseEvent) => void) | undefined
}

/**
 * What a meeting offers right now, in priority order: View Transcript while it
 * records, Transcribe once the call is detected, Join until the meeting ends,
 * and Transcribe inside the join window for a meeting with no link to join.
 * View Summary rides alongside once a note from earlier today is saved.
 *
 * Mounts the live-audio feeds and the linked-notes query. Call it from a
 * surface open on one meeting, or a row inside its join window, never once
 * per row.
 */
export function useMeetingCallsToAction(meeting: CalendarEvent, trigger: string): MeetingCallsToAction {
  const { joinLink, joinLabel, isAwaitingJoin, handleJoinClick } = useJoinMeeting(meeting, trigger)
  const linkedAudioNotes = useLinkedAudioNotes(meeting)
  const isTakingNotes = useIsTakingNotes()
  const detectedMeeting = useDetectedMeetingForEvent(meeting)
  const isTranscribingThisMeeting = useIsRecordingCalendarEvent(meeting)
  const liveMeeting = useLiveMeetingIdentity()
  const { openView } = useViewActions()
  const { startAudioNote } = useStartAudioNote()
  const openSummary = useOpenMeetingSummary(meeting)

  const isHappeningNow = isWithinNextMinutes(meeting.start, 1, meeting.end)
  const isJoinable = isJoinWindowOpen(meeting)
  const isOver = !!meeting.end && new Date(meeting.end).getTime() < Date.now()
  const summaryAudioNote = useMemo(() => findSummaryReadyNote(linkedAudioNotes, meeting), [linkedAudioNotes, meeting])

  const transcribe = (meetingToRecord: RecallDetectedMeeting | GoogleMeetMeeting) => (event: MouseEvent) => {
    event.stopPropagation()
    AmplitudeRenderer.trackEvent('Audio Note Action', { action: 'start transcript from meeting list', trigger })
    void startAudioNote({ source: 'base', meeting: meetingToRecord })
  }

  const primary = ((): MeetingCallToAction | undefined => {
    if (isTranscribingThisMeeting) {
      return {
        label: 'View Transcript',
        isProminent: true,
        onSelect: (event) => openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel: 'Home', pointerEvent: event }),
      }
    }
    if (detectedMeeting && !isTakingNotes) {
      return { label: 'Transcribe', isProminent: isHappeningNow, onSelect: transcribe(detectedMeeting) }
    }
    // Matches the Meeting View header's Join, which lasts until the scheduled
    // end and hides once a note exists. While another meeting records, its View
    // Transcript keeps the inverse fill, so back-to-back calls stay distinct.
    if (joinLink && !isOver && linkedAudioNotes.length === 0) {
      return {
        label: joinLabel,
        isProminent: isJoinable && !isTakingNotes,
        isEarly: !isJoinable,
        isPending: isAwaitingJoin,
        onSelect: handleJoinClick,
      }
    }
    // Recording the calendar event's own shape (id = event id) is what flips
    // this to View Transcript once it starts. Not offered over another
    // recording, which a manual start won't interrupt.
    if (!joinLink && isJoinable && !isTakingNotes) {
      return {
        label: 'Transcribe',
        isProminent: isHappeningNow,
        onSelect: transcribe(calendarEventToScheduledMeeting(meeting)),
      }
    }
    return undefined
  })()

  const viewSummary =
    summaryAudioNote && !isTranscribingThisMeeting
      ? (event: MouseEvent) => openSummary(summaryAudioNote.id, trigger, { pointerEvent: event })
      : undefined

  return { primary, viewSummary }
}
